import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Stack, Typography } from "@mui/material";
import type { SupportFlowResult } from "Components/Support/SupportFlowResult";
import {
  Resources,
  type ResourceDictionary,
  useResource,
} from "Translations/Resources";
import type { ObjPathProxy } from "ts-object-path";
import type { ReactNode } from "react";

const ICON_FONT_SIZE = 64;

/**
 * At module scope, with the title held as a resource path and translated at the use site —
 * the same arrangement as CATEGORY_RESOURCES in SupportRequestCategory.ts. Sharing the icon
 * elements across renders is safe because both are static and stateless.
 *
 * The role is what announces the outcome: this screen is the only confirmation a submit
 * produces, and the toast it replaced was announced by sonner's live region.
 */
const VARIANT_CONTENT: Record<
  SupportFlowResult["variant"],
  {
    icon: ReactNode;
    titleTKey: ObjPathProxy<ResourceDictionary, string>;
    role: "status" | "alert";
  }
> = {
  success: {
    icon: (
      <CheckCircleOutlineRoundedIcon
        // `success.main` is Colors.Green (#00C44E); `success.dark` reads noticeably duller
        // at this size.
        sx={{ fontSize: ICON_FONT_SIZE, color: "success.main" }}
      />
    ),
    titleTKey: Resources.Common.ThankYou,
    role: "status",
  },
  error: {
    icon: (
      <ErrorOutlineRoundedIcon
        color="error"
        sx={{ fontSize: ICON_FONT_SIZE }}
      />
    ),
    titleTKey: Resources.Support.Result.ErrorTitle,
    role: "alert",
  },
};

type Props = {
  result: SupportFlowResult;
};

export const SupportFlowResultStep = ({ result }: Props) => {
  const { t } = useResource();
  const { icon, titleTKey, role } = VARIANT_CONTENT[result.variant];

  return (
    // Desktop already has the gap from ModalDialog's header row; the drawer renders no
    // title row on this step, so there the space has to come from here. `md` is the same
    // 1050px boundary the dialog switches to a sheet at.
    <Stack
      alignItems="center"
      gap={2}
      pt={{ xs: 2, md: 0 }}
      pb={{ xs: 0, md: 6 }}
      role={role}
    >
      {icon}
      <Typography variant="h6" textAlign="center">
        {t(titleTKey)}
      </Typography>
      <Typography color="text.secondary" textAlign="center">
        {result.message}
      </Typography>
    </Stack>
  );
};
